/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {map, catchError, tap} from 'rxjs/operators';
import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {Observable, of} from "rxjs";
import {environment} from '../../environments/environment';
import {APP_CONFIG} from '../app.config';
import {Accident} from "../model/Accident";
import {AccidentVehicle} from "../model/accident-vehicle";
import {MessageService} from "./message.service";
import {AccidentVehiclePerson} from "../model/accident-vehicle-person";
import {Driver} from "../model/Driver";
import {Attachment} from "../model/attachment";

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable()
export class AccidentsService {

  private readonly baseUrl: string;

  constructor(private http: HttpClient, private messageService: MessageService) {
    this.baseUrl = inject(APP_CONFIG).baseUrl + '/api';
  }

  getAccidentsByDriver(driverId): Observable<Accident[]> {
    const id = typeof driverId === 'number' ? driverId : driverId;
    const url = `${this.baseUrl}/drivers/${id}/accidents`;

    return this.http.get<Accident[]>(url).pipe(
      tap(_ => this.log('got Driver Accidents "${driver}"')),
      catchError(this.handleError<Accident[]>('getDriverAccidents'))
    );
  }

  getAccidentById(accidentId): Observable<Accident> {
    const id = typeof accidentId === 'number' ? accidentId : accidentId;
    const url = `${this.baseUrl}/accidents/${id}`;

    return this.http.get<Accident>(url).pipe(
      tap(_ => this.log('got accident by Id "${accident}"')),
      catchError(this.handleError<Accident>('getAccidentById'))
    );
  }

  addAccidentInfo(accident: Accident): Observable<Accident>{
    const url = `${this.baseUrl}/accidents`;

    return this.http.post(url,accident,httpOptions).pipe(
      map(accident => accident as Accident));
  }

  updateAccidentById(accident: Accident): Observable<Accident>{
    const id = accident.accidentId;
    const url = `${this.baseUrl}/accidents/${id}`;

    return this.http.put(url,accident,httpOptions).pipe(
      map(accident => accident as Accident));
  }

  deleteAccidentById(accident: Accident): Observable<{}> {
    const id = accident.accidentId;
    const url = `${this.baseUrl}/accidents/${id}`;
    return this.http.delete(url, httpOptions)
      .pipe(
        catchError(this.handleError('delete Accident'))
      );
  }

  updateAccidentAttachmentById(accidentId:number, attachment: Attachment): Observable<{}>{
    const url = `${this.baseUrl}/accidents/${accidentId}/attachment`;
    return this.http.put(url,attachment,httpOptions).pipe(
      map(obj=> obj as any));
  }

  addAccidentVehicle(accidentVehicle: AccidentVehicle, accidentId:number): Observable<AccidentVehicle>{
    const url = `${this.baseUrl}/accidents/${accidentId}/accidentvehicles`;

    return this.http.post(url,accidentVehicle,httpOptions).pipe(
      map(accidentVehicle => accidentVehicle as AccidentVehicle));
  }

  updateAccidentVehicleById(accidentVehicle: AccidentVehicle): Observable<AccidentVehicle>{
    const id = accidentVehicle.accidentVehicleId;
    const url = `${this.baseUrl}/accidentvehicles/${id}`;

    return this.http.put(url,accidentVehicle,httpOptions).pipe(
      map(accidentVehicle => accidentVehicle as AccidentVehicle));
  }

  addAccidentPerson(accidentVehiclePerson: AccidentVehiclePerson, accidentVehicleId:number): Observable<AccidentVehiclePerson>{
    const url = `${this.baseUrl}/accidentvehicles/${accidentVehicleId}/accidentvehiclepersons`;

    return this.http.post(url,accidentVehiclePerson,httpOptions).pipe(
      map(accidentVehiclePerson => accidentVehiclePerson as AccidentVehiclePerson));
  }

  updateAccidentPersonById(accidentVehiclePerson: AccidentVehiclePerson): Observable<AccidentVehiclePerson>{
    const id = accidentVehiclePerson.accidentVehiclePersonId;
    const url = `${this.baseUrl}/accidentvehiclepersons/${id}`;

    return this.http.put(url,accidentVehiclePerson,httpOptions).pipe(
      map(accidentVehiclePerson => accidentVehiclePerson as AccidentVehiclePerson));
  }

  deleteAccidentPersonById(accidentVehiclePerson: AccidentVehiclePerson): Observable<{}> {
    // let cpHeaders = new Headers({ 'Content-Type': 'application/json' });
    // let options = new RequestOptions({ headers: cpHeaders });
    const id = accidentVehiclePerson.accidentVehiclePersonId;
    const url = `${this.baseUrl}/accidentvehiclepersons/${id}`;
    return this.http.delete(url, httpOptions)
      .pipe(
        catchError(this.handleError('delete AccidentVehiclePerson'))
      );
  }

  deleteAccidentVehicleById(accidentVehicle: AccidentVehicle): Observable<{}> {
    // let cpHeaders = new Headers({ 'Content-Type': 'application/json' });
    // let options = new RequestOptions({ headers: cpHeaders });
    const id = accidentVehicle.accidentVehicleId;
    const url = `${this.baseUrl}/accidentvehicles/${id}`;
    return this.http.delete(url, httpOptions)
      .pipe(
        catchError(this.handleError('delete Accident Vehicle By Id'))
      );
  }

  getAccidentPersonsByVehicleId(accidentVehicleId): Observable<AccidentVehiclePerson[]> {
    const id = typeof accidentVehicleId === 'number' ? accidentVehicleId : accidentVehicleId;
    const url = `${this.baseUrl}/accidentvehicles/${id}/accidentvehiclepersons`;

    return this.http.get<AccidentVehiclePerson[]>(url).pipe(
      tap(_ => this.log('got accident by Id "${AccidentVehiclePerson}"')),
      catchError(this.handleError<AccidentVehiclePerson[]>('AccidentVehiclePerson'))
    );
  }

  getAccidentPersonByPersonId(personId): Observable<Driver> {
    const id = typeof personId === 'number' ? personId : personId;
    const url = `${this.baseUrl}/accidentpersons/${id}`;

    return this.http.get<Driver>(url).pipe(
      tap(_ => this.log('got accident by Id "${AccidentVehiclePerson}"')),
      catchError(this.handleError<Driver>('AccidentVehiclePerson'))
    );
  }

  getAccidentVehiclesById(accidentId): Observable<AccidentVehicle[]> {
    const id = typeof accidentId === 'number' ? accidentId : accidentId;
    const url = `${this.baseUrl}/accidents/${id}/accidentvehicles`;

    return this.http.get<AccidentVehicle[]>(url).pipe(
      tap(_ => this.log('got accident by Id "${accident vehicles}"')),
      catchError(this.handleError<AccidentVehicle[]>('getAccidentVehiclesById'))
    );
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // TODO: send the error to remote logging infrastructure
      // console.error(error); // log to console instead
      if (error.error.errorCode) {
        if (error.error.errorCode !== '404 Not Found')
          alert(error.error.errorCode + " : " + error.error.errorMessage);
      }
      else
        alert(error.status + " : " + error.error.message);


      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  /** Log a HeroService message with the MessageService */
  private log(message: string) {
    this.messageService.add('Driver: ' + message);
  }
}

